<template>
  <component
    :is="tag"
    :aria-busy="isLoading"
    :aria-disabled="isDisabled"
    :class="{
      [$style['action']]: true,
      [$style['action--disabled']]: isDisabled,
      [$style['action--busy']]: isLoading,
    }"
    :data-testid="testId"
    :disabled="isDisabled"
    :form="form"
    :style="`
    --__width: ${width};
    --__height: ${height};
    --__background-color: ${theme?.backgroundColor ?? Color.primary700};
    --__background-color-hover: ${theme?.backgroundColorHover ?? Color.primary600};
    --__text-color: ${theme?.textColor ?? Color.mono100}`"
    :type="type"
    v-bind="$attrs"
    @click="$emit('click')"
  >
    <template v-if="$slots.icon && !isLoading">
      <slot
        :class="$style['action__icon']"
        name="icon"
      />
    </template>
    <span :class="$style['action__inner']">
      <slot v-if="!isLoading" />
      <span
        v-else
        :class="$style['action__loading']"
      >
        <LoadingBubble :color="theme?.loadingColor ?? '#ffffff'" />
      </span>
    </span>
  </component>
</template>

<script lang="ts">
import { actionProps } from '../actionProps';
import { ActionState } from '../ActionState';
import { Color } from 'assets/colors/colors';
import { NuxtLink } from '#components';
import { LoadingBubble } from '~/components-daisy/loading';

export default defineComponent({
  name: 'ActionPrimary',
  components: {
    LoadingBubble,
    NuxtLink,
  },
  inheritAttrs: false,
  props: {
    ...actionProps,
    tag: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: false,
      default: '',
    },
    width: {
      type: String,
      required: false,
      default: '20.313rem',
    },
    height: {
      type: String,
      required: false,
      default: '3.125rem',
    },
    form: {
      type: String,
      required: false,
      default: () => undefined,
    },
    theme: {
      type: Object as PropType<{
        backgroundColor?: string;
        backgroundColorHover?: string;
        textColor?: string;
        loadingColor?: string;
      }>,
      required: false,
      default: () => ({}),
    },
  },
  emits: ['click'],
  setup(props) {
    const isDisabled = computed(() => {
      switch (props.state) {
        case ActionState.Idle:
          return false;
        case ActionState.Busy:
          return true;
        case ActionState.Disabled:
          return true;
        default:
          return undefined;
      }
    });

    const isLoading = computed(() => {
      switch (props.state) {
        case ActionState.Idle:
          return false;
        case ActionState.Busy:
          return true;
        case ActionState.Disabled:
          return false;
        default:
          return undefined;
      }
    });

    return {
      isDisabled,
      isLoading,
      Color,
    };
  },
});
</script>

<style lang="postcss" module>
.action {
  justify-content: center;
  align-items: center;
  display: flex;
  gap: var(--space-100);
  outline: none;
  width: var(--__width);
  min-width: 8.125rem;
  max-width: 20.313rem;
  border-radius: var(--border-radius-600);
  box-shadow: var(--shadow-200);
  height: var(--__height);
  padding: var(--space-100) var(--space-200);
  border: none;
  font: var(--font-400);
  font-size: 16px;
  -moz-osx-font-smoothing: antialiased;
  -webkit-font-smoothing: antialiased;
  cursor: pointer;
  touch-action: manipulation;
  background: var(--__background-color);
  color: var(--__text-color);

  &--disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &--busy {
    cursor: wait;
  }

  &:focus-visible {
    text-decoration: underline;
  }

  &:hover {
    transition: background-color 400ms;
    background: var(--__background-color-hover);
  }

  &__icon {
    display: flex;
  }

  &__inner {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    vertical-align: middle;
  }

  &__loading {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
